import { yupResolver } from "@hookform/resolvers/yup";
import Archive from "@mui/icons-material/Archive";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { capitalize, groupBy, isEmpty, toPairs } from "lodash-es";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useAsyncStateSupabaseCallback } from "../../../../utils/hooks/use-async-state-callback";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { propertyNotEmpty } from "../../../../utils/not-empty";
import yup, { yupUUID } from "../../../../utils/yup";
import { SupabaseEmailDesignMomentInfoService } from "../../../moment/server/supabase-moment-service";
import { EmailDesignSelector } from "../email-design-selector";

import type { UUID } from "../../../../utils/uuid";
import type { MomentStatus } from "../../../moment-card/types";
import type { EmailDesign } from "../../server/supabase-email-design-service";
import type { ButtonProps } from "@mui/material";
import type { FC, ReactNode } from "react";

export const ArchiveButton: FC<
  {
    archive: (new_id: UUID) => void;
    archiving: boolean;
    emailDesign: EmailDesign;
    allDesigns: EmailDesign[];
  } & ButtonProps
> = ({ archive, archiving, emailDesign, allDesigns, ...props }) => {
  const dialog = useDialog<{ id: UUID; title: string; status: MomentStatus }[]>();

  const { loading: momentsLoading, load: loadMoments } = useAsyncStateSupabaseCallback(
    async ({ supabase, account_id }, design: EmailDesign) => {
      if (!design) return [];

      const { data, error } = await new SupabaseEmailDesignMomentInfoService(supabase).getAll(
        account_id,
        {
          filter: [
            {
              key: "email_design_id",
              operator: "eq",
              value: design.id,
            },
          ],
          order: [{ column: "id" }],
        },
      );

      if (error) throw error;

      return data
        .filter(propertyNotEmpty("status"))
        .filter(propertyNotEmpty("title"))
        .filter(propertyNotEmpty("id"));
    },
    [],
  );

  const { handleSubmit, control, getValues, reset } = useForm<{ new_email_design_id: UUID }>({
    defaultValues: {},
    resolver: yupResolver(yup.object({ new_email_design_id: yupUUID.required() })),
  });

  useWatch({ control, name: "new_email_design_id" });
  const new_email_design_id = getValues("new_email_design_id");

  return (
    <>
      <Tooltip title="Archive Design" placement="top" arrow>
        <span>
          <Button
            data-analytics-id="email-design-archive"
            type="button"
            variant="outlined"
            color="secondary"
            id="archiveEmailDesignButton"
            disabled={momentsLoading || archiving}
            loading={momentsLoading || archiving}
            onClick={() =>
              void loadMoments(emailDesign)
                .then((data) => {
                  reset({
                    // If there are no moments, then we can just archive the design with any ID other than the current one
                    // as nothing will be impacted
                    new_email_design_id:
                      data && data.length > 0
                        ? undefined
                        : allDesigns.find((x) => x.id !== emailDesign.id)!.id,
                  });
                  dialog.handleOpen(data);
                })
                .catch(console.error)
            }
            {...props}
            startIcon={<Archive />}
          >
            Archive
          </Button>
        </span>
      </Tooltip>
      <Dialog open={dialog.open} onClose={dialog.handleClose}>
        <DialogTitle>Archive Email Design</DialogTitle>
        <form onSubmit={handleSubmit((data) => archive(data.new_email_design_id))}>
          {dialog.data && (
            <>
              <DialogContent>
                <Stack spacing={1}>
                  <HasMomentsWarning templateName={emailDesign.name} moments={dialog.data} />
                  {dialog.data.length > 0 && (
                    <>
                      <DialogContentText>
                        In order to archive this template, you must first reassign the
                        Communications to another template:
                      </DialogContentText>
                      <Controller
                        name="new_email_design_id"
                        control={control}
                        render={({ field }) => (
                          <EmailDesignSelector
                            value={allDesigns.find((x) => x.id === field.value)}
                            emailDesigns={allDesigns.filter((x) => x.id !== emailDesign.id)}
                            onChange={(x) => x.type === "existing" && field.onChange(x.id)}
                            showConfirmDialog={false}
                            hasMaxTemplates={false}
                            hideCreateNew={true}
                          />
                        )}
                      />
                    </>
                  )}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button
                  data-analytics-id="email-design-archive-dialog-cancel"
                  loading={archiving || momentsLoading}
                  onClick={dialog.handleClose}
                  variant="text"
                  color="primary"
                >
                  Cancel
                </Button>
                <Button
                  data-analytics-id="email-design-archive-dialog-archive"
                  loading={archiving || momentsLoading}
                  disabled={!new_email_design_id}
                  variant="contained"
                  type="submit"
                  color="error"
                >
                  Archive
                </Button>
              </DialogActions>
            </>
          )}
        </form>
      </Dialog>
    </>
  );
};

const HasMomentsWarning: FC<{
  templateName: string;
  moments: { id: UUID; title: string; status: MomentStatus }[];
}> = ({ templateName, moments }) => {
  const dialog = useDialog();

  const grouped_moments = groupBy(moments, (x) => x.status);
  const activeCount = grouped_moments.active?.length ?? 0;
  const countLabel = `${moments.length} Communication${moments.length === 1 ? "" : "s"}`;
  const activeLabel = `${activeCount} ${activeCount === 1 ? "is" : "are"} active`;

  return (
    <>
      <DialogContentText>
        {`Are you sure you want to archive the "${templateName}" email template?`}
      </DialogContentText>
      <DialogContentText>
        There {moments.length === 1 ? "is" : "are"} currently{" "}
        <GroupsTooltip grouped_moments={grouped_moments}>{countLabel}</GroupsTooltip> using this
        template
        {moments.length > 0 ? (
          <>
            {", of which "}
            {activeCount > 0 ? (
              <OpenDialogLink onClick={dialog.handleOpen}>{activeLabel}</OpenDialogLink>
            ) : (
              <span>{activeLabel}</span>
            )}
          </>
        ) : null}
        .
      </DialogContentText>
      <Dialog open={dialog.open} onClose={dialog.handleClose}>
        <DialogTitle>Impacted Active Communications</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            The following active Communications will be impacted by this change:
          </Typography>
          <ul>
            {(grouped_moments.active ?? []).map((moment) => (
              <li key={moment.id}>
                <Link href={`/moments/${moment.id}`} target="_blank">
                  {moment.title}
                </Link>
              </li>
            ))}
          </ul>
        </DialogContent>
        <DialogActions>
          <Button
            data-analytics-id="email-design-archive-moments-warning-close"
            onClick={dialog.handleClose}
            variant="text"
            color="primary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const GroupsTooltip: FC<{
  grouped_moments: Record<
    string,
    {
      id: UUID;
      title: string;
      status: MomentStatus;
    }[]
  >;
  children: ReactNode;
}> = ({ grouped_moments, children }) => {
  if (isEmpty(grouped_moments)) return <>{children}</>;

  return (
    <Tooltip
      placement="top"
      title={
        <List>
          {toPairs(grouped_moments).map(([status, moments_]) => (
            <ListItem key={status}>
              {capitalize(status)}: {moments_.length}
            </ListItem>
          ))}
        </List>
      }
    >
      <span style={{ cursor: "pointer", color: "#5048e5" }}>{children}</span>
    </Tooltip>
  );
};

const OpenDialogLink: FC<{ onClick: () => void; children: ReactNode }> = ({
  onClick,
  children,
}) => (
  <Tooltip title="Click to see the impacted active Communications">
    <Button
      data-analytics-id="email-design-archive-dialog-impacted-moments"
      variant="text"
      color="primary"
      onClick={onClick}
    >
      {children}
    </Button>
  </Tooltip>
);
